// @file Helper functions for wall collections.
import { __ } from 'bits/intl'
import { WallsFilter } from 'types/collections'

export const LIBRARY_ACCOUNT_FILTERS: WallsFilter[] = ['all', 'made', 'private_walls', 'archived']
export const USER_ACCOUNT_FILTERS: WallsFilter[] = ['all', 'archived']
export const TENANT_USER_ACCOUNT_FILTERS: WallsFilter[] = ['all', 'made', 'archived']

export function filterDisplayName(filter: WallsFilter): string {
  switch (filter) {
    case 'recents':
    case 'combined_recents':
    case 'writableRecents':
      return __('Recents')
    case 'all':
      return __('All')
    case 'made':
      return __('Made')
    case 'shared':
    case 'combined_shared':
      return __('All')
    case 'writableShared':
      return __('Shared')
    case 'network':
    case 'writableNetwork':
      return __('Network')
    case 'liked':
    case 'writableLiked':
      return __('Liked')
    case 'archived':
      return __('Archived')
    case 'favorites':
      return __('Favorites')
    case 'gallery':
      return __('Gallery')
    case 'private_walls':
      return __('Private')
    // Fill these in when it's actually being used
    case 'portfolio':
    case 'contributed':
    default:
      return ''
  }
}

export const GalleryTextToVizMap = {
  all: 'all',
  wall: 'grid',
  stream: 'stream',
  grid: 'matrix',
  shelf: 'shelf',
  map: 'map',
  canvas: 'free',
  timeline: 'timeline',
}

export const galleryVizArray = Object.values(GalleryTextToVizMap)
export const galleryTextArray = Object.keys(GalleryTextToVizMap)

export const galleryVizes = [
  { icon: 'gallery_outline', text: __('All'), viz: 'all' },
  { icon: 'wall_outline', text: __('Wall'), viz: 'grid' },
  { icon: 'stream_outline', text: __('Stream'), viz: 'stream' },
  { icon: 'grid_outline', text: __('Grid'), viz: 'matrix' },
  { icon: 'shelf_outline', text: __('Shelf'), viz: 'shelf' },
  { icon: 'map_outline', text: __('Map'), viz: 'map' },
  { icon: 'canvas_outline', text: __('Canvas'), viz: 'free' },
  { icon: 'timeline_outline', text: __('Timeline'), viz: 'timeline' },
]

export function filterIconName(filter: WallsFilter): string {
  const knownIcons = {
    all: 'gallery_outline',
    recents: 'recent_outline',
    made: 'made_outline',
    shared: 'handshake_outline',
    liked: 'heart_outline_thin',
    archived: 'archive_outline',
    gallery: 'compass',
    private_walls: 'password',
  }
  return knownIcons[filter] || 'folder_outline'
}

export function getEmptyFilterCollectionCopy(filter: WallsFilter): [string, string] {
  switch (filter) {
    case 'recents':
    case 'combined_recents':
    case 'writableRecents':
      return [__('No recent padlet activity.'), __('This too shall pass.')]
    case 'made':
      return [__("You haven't made any padlets yet."), __('Go create some magic.')]
    case 'shared':
    case 'combined_shared':
    case 'combined_shared_user':
    case 'combined_shared_library':
    case 'writableShared':
      return [__('No shared padlets yet.'), __('Sharing is caring.')]
    case 'liked':
    case 'writableLiked':
      return [__("You haven't liked any padlets yet."), __('Go ahead, show your favourite padlets some love.')]
    case 'archived':
      return [__("You haven't archived any padlets yet."), __("There's no rush, but it's here when you need it.")]
    case 'favorites':
      return [__("You haven't bookmarked any padlets yet."), __('This is a safe space for the padlets you care about.')]
    case 'gallery':
      return [
        "Uh oh. You shouldn't be seeing this. The gallery is empty.",
        "Run `yarn dev:recreatedb` if you're on dev",
      ]
    case 'all':
      return [__('No padlets yet.'), __('Why wait? Make something great.')]
    case 'private_walls':
      return [__("You don't have any private padlets."), __('Create something just for you.')]
    case 'portfolio':
    case 'contributed':
    case 'network':
    case 'writableNetwork':
    default:
      return ['', '']
  }
}

export function getEmptyFolderCollectionCopy(): [string, string] {
  return [__("You haven't added any padlets to this folder."), __('Ready whenever you are.')]
}

export function getEmptySearchCollectionCopy(): [string, string] {
  return [__('No results found.'), __('We hope you find what you are looking for.')]
}
